import React, { useEffect, useState } from 'react';
import Alert from '@amzn/awsui-components-console/alert';
import Badge from '@amzn/awsui-components-console/badge';
import Box from '@amzn/awsui-components-console/box';
import Modal from '@amzn/awsui-components-console/modal';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Spinner from '@amzn/awsui-components-console/spinner';
import Table from '@amzn/awsui-components-console/table';
import {
  useDescribeStringGroupLazyQuery,
  useListStringsLazyQuery,
} from 'src/generated/graphql';
import { StringGroupVersionSummary, StringInfo } from '../types';
import { formatDayMonthYear } from 'src/common/utils/dateFormatters';
import { fetchVersionSnapshot } from '../utils/fetchVersionSnapshot';
import { describeLoadFailure } from '../utils/snapshotError';
import styles from '../StringGroupDetail.module.scss';
import AccessDeniedAlert from 'src/common/authz/AccessDeniedAlert';
import {
  getAccessDeniedMessage,
  getAccessDeniedNamespace,
  isAccessDenied,
} from 'src/common/authz/isAccessDenied';
import { useTranslation } from 'src/i18n/shim/useTranslation';

interface ViewVersionModalProps {
  visible: boolean;
  version: number;
  namespaceId: string;
  stringGroupId: string;
  versionMeta?: StringGroupVersionSummary;
  onDismiss: () => void;
}

function formatDate(locale: string, timestamp: number): string {
  // Missing server timestamps arrive as 0, which would otherwise render as
  // 01 Jan 1970 — match the em-dash placeholder the other panels use.
  if (!timestamp) {
    return '—';
  }
  return formatDayMonthYear(locale, new Date(timestamp));
}

function mergeStrings(target: Map<string, StringInfo>, incoming: StringInfo[]) {
  for (const stringInfo of incoming) {
    const existing = target.get(stringInfo.stringId);
    if (!existing) {
      target.set(stringInfo.stringId, stringInfo);
      continue;
    }
    const variants = new Map(
      existing.variants.map((variant) => [variant.cxId, variant]),
    );
    stringInfo.variants.forEach((variant) =>
      variants.set(variant.cxId, variant),
    );
    target.set(stringInfo.stringId, {
      ...existing,
      ...stringInfo,
      variants: [...variants.values()],
    });
  }
}

const ViewVersionModal: React.FC<ViewVersionModalProps> = ({
  visible,
  version,
  namespaceId,
  stringGroupId,
  versionMeta,
  onDismiss,
}) => {
  const { t, locale } = useTranslation();
  const [fetchStrings] = useListStringsLazyQuery();
  const [fetchGroupVersion] = useDescribeStringGroupLazyQuery();
  const [strings, setStrings] = useState<StringInfo[]>([]);
  const [cxIds, setCxIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  // Held as thrown rather than coerced: only a real upstream message can fill the "{detail}"
  // slot, so a message-less throwable gets the frameless stand-in instead of a nested frame.
  const [error, setError] = useState<{ thrown: unknown } | null>(null);

  useEffect(() => {
    if (!visible) return;

    // Drop the previous open's rows before the fetch starts. `loading` is only
    // set inside the async body below, so on the first render after reopening
    // for a different version the retained rows would otherwise show under the
    // new header for a frame. Clearing here also means `renderContent` never
    // shows one version's strings while another is being fetched.
    setStrings([]);
    setLoading(true);

    let cancelled = false;
    const loadSnapshot = async () => {
      setLoading(true);
      setError(null);
      const byStringId = new Map<string, StringInfo>();

      try {
        // The shared paginated walk, so one version renders identically wherever it is read.
        const flat = await fetchVersionSnapshot(
          fetchStrings,
          namespaceId,
          stringGroupId,
          version,
        );
        // Union variants for a stringId the backend split across pages.
        mergeStrings(byStringId, flat);

        if (!cancelled) {
          setStrings(
            [...byStringId.values()].sort((left, right) =>
              left.stringId.localeCompare(right.stringId),
            ),
          );
        }
      } catch (loadError) {
        if (!cancelled) {
          setError({ thrown: loadError });
          setStrings([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void loadSnapshot();
    return () => {
      cancelled = true;
    };
    // No `t` dependency: guard failures are thrown as SnapshotErrors and worded at render, so a
    // locale change does not re-fetch the snapshot.
  }, [visible, version, namespaceId, stringGroupId, fetchStrings]);

  // The version's CX ID set is fetched separately from the strings snapshot:
  // describeStringGroup is a fast version-aware metadata read that already
  // returns the cxIds recorded for that version, so the "CX IDs at this
  // version" section stays accurate per version without extending the
  // version-history query. It is intentionally NOT gated by the strings
  // spinner/error below — the list is supplementary, so a describeStringGroup
  // failure leaves it empty rather than failing the whole snapshot.
  useEffect(() => {
    if (!visible) return;

    setCxIds([]);
    let cancelled = false;
    const loadCxIds = async () => {
      try {
        const result = await fetchGroupVersion({
          variables: {
            namespaceId,
            stringGroupId,
            stringGroupVersion: version.toString(),
          },
          fetchPolicy: 'network-only',
        });
        if (!cancelled && result.data?.describeStringGroup) {
          // De-duplicate: describeStringGroup.cxIds is not guaranteed unique,
          // so keying badges directly off it can emit duplicate React keys and
          // render duplicate badges (the sibling describeStringMock de-dupes too).
          setCxIds([...new Set(result.data.describeStringGroup.cxIds)]);
        }
      } catch {
        // Supplementary data — leave the CX ID list empty on failure.
      }
    };

    void loadCxIds();
    return () => {
      cancelled = true;
    };
  }, [visible, version, namespaceId, stringGroupId, fetchGroupVersion]);

  const renderContent = () => {
    if (loading) {
      return (
        <Box textAlign="center" padding="l">
          <Spinner size="large" />
          <Box variant="p" color="text-body-secondary" margin={{ top: 's' }}>
            {t('sgd-view-version-loading')}
          </Box>
        </Box>
      );
    }

    if (error) {
      // A refusal is not a load failure, so it drops the "Failed to load
      // strings:" prefix and shows the service's own wording alone.
      const failure = describeLoadFailure(t, error.thrown);
      return isAccessDenied(error.thrown) ? (
        <AccessDeniedAlert
          message={getAccessDeniedMessage(error.thrown)}
          deniedNamespace={getAccessDeniedNamespace(error.thrown)}
          data-testid="view-version-access-denied"
        />
      ) : (
        <Alert type="error">
          {/* Only the service's own wording goes in the "{detail}" slot. Our
              own guard copy is already a complete sentence, so framing it would
              wrap it in a second sentence saying the same thing; a throwable
              with no wording at all gets the frameless stand-in. */}
          {failure.kind === 'upstream'
            ? t('sgd-version-strings-load-failed', { detail: failure.text })
            : failure.kind === 'own'
              ? failure.text
              : t('sgd-version-snapshot-load-failed')}
        </Alert>
      );
    }

    return (
      <SpaceBetween size="m">
        {versionMeta && (
          <Box color="text-body-secondary" fontSize="body-s">
            {t('sgd-view-version-snapshot-meta', {
              date: formatDate(locale, versionMeta.updatedOn),
              user: versionMeta.updatedBy,
              count: versionMeta.stringCount,
            })}
          </Box>
        )}

        {versionMeta?.cancelRequestedOn && (
          <Box color="text-status-inactive" fontSize="body-s">
            {/* Two whole sentences, not one plus an appended " by {user}": the
                actor's position differs by language, and a trailing fragment
                cannot move. */}
            {versionMeta.cancelRequestedBy
              ? t('sgd-view-version-cancel-requested-on-by', {
                  date: formatDate(locale, versionMeta.cancelRequestedOn),
                  user: versionMeta.cancelRequestedBy,
                })
              : t('sgd-view-version-cancel-requested-on', {
                  date: formatDate(locale, versionMeta.cancelRequestedOn),
                })}
          </Box>
        )}

        <SpaceBetween size="xs">
          <Box fontWeight="bold">{t('sgd-version-cx-ids-at')}</Box>
          {cxIds.length > 0 ? (
            <div className={styles.cxIdBadgeList}>
              {cxIds.map((cxId) => (
                <Badge key={cxId} color="grey">
                  {cxId}
                </Badge>
              ))}
            </div>
          ) : (
            <Box color="text-body-secondary" fontSize="body-s">
              &mdash;
            </Box>
          )}
        </SpaceBetween>

        <SpaceBetween size="xs">
          <Box fontWeight="bold">{t('sgd-strings-heading')}</Box>
          <Table
            ariaLabels={{ tableLabel: t('sgd-version-strings-table-aria') }}
            columnDefinitions={[
              {
                id: 'stringId',
                header: t('string-field-string-id'),
                cell: (item: StringInfo) => (
                  <Box fontWeight="bold">{item.stringId}</Box>
                ),
                width: 200,
              },
              {
                id: 'sourceText',
                header: t('sgd-column-source-text'),
                cell: (item: StringInfo) => item.sourceText,
              },
            ]}
            items={strings}
            stripedRows
            variant="embedded"
            empty={
              <Box textAlign="center" color="inherit" padding="l">
                {t('sgd-version-strings-empty')}
              </Box>
            }
          />
        </SpaceBetween>
      </SpaceBetween>
    );
  };

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      size="large"
      header={t('sgd-view-version-modal-header', { version })}
    >
      {renderContent()}
    </Modal>
  );
};

export default ViewVersionModal;
